/**
 * AreasPage — manage areas and their postcodes (Settings → Areas).
 *
 * An area holds a list of postcodes and, when the Regions module is enabled
 * (clientConfig.features.regions — SC-6259), belongs to an optional parent
 * region. Teams assigned to an area cover its postcodes for availability.
 */

import { clientConfig } from '@/config/clientConfig';
import { Can } from '@/domains/access-control';
import { PageHeader } from '@/shared/components';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@/shared/ui';
import type { Area } from '@skewb-works/contracts';
import { useCan } from '@skewb/access-control/react';
import { ChevronLeft, ChevronRight, MapPin, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { locationsService } from '../api/locationsService';
import {
  useAreas,
  useCreateArea,
  useDeleteArea,
  useRegions,
  useUpdateArea,
} from '../hooks/useLocations';

// Sentinel for the "no region" option — Radix Select can't hold an empty value.
const NO_REGION = '__none__';

// Split a free-text postcode entry (newlines / commas) into a clean list.
function parsePostcodes(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\n,]/)
        .map((p) => p.trim().toUpperCase())
        .filter((p) => p.length > 0),
    ),
  );
}

const PAGE_SIZE = 25;

export function AreasPage() {
  // Regions module (SC-6259): only opted-in brands see the region column/select.
  const regionsEnabled = clientConfig.features.regions;
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  // Debounce the search term so each keystroke doesn't fire a request.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useAreas({
    search: debouncedSearch || undefined,
    limit: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  });
  const areas = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const { data: regions = [] } = useRegions({ enabled: regionsEnabled });
  const createMutation = useCreateArea();
  const [editingId, setEditingId] = useState<number | null>(null);
  const updateMutation = useUpdateArea(editingId ?? 0);
  const deleteMutation = useDeleteArea();
  // Drop the always-empty Actions column when the role can neither edit nor delete (WRK-5669).
  const canEditArea = useCan('update', 'Area');
  const canDeleteArea = useCan('delete', 'Area');
  const showActions = canEditArea || canDeleteArea;
  // Column count for full-width message rows: Name + Postcodes, plus Region (when enabled)
  // and Actions (when shown).
  const colSpan = 2 + (regionsEnabled ? 1 : 0) + (showActions ? 1 : 0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [contractor, setContractor] = useState('');
  // Held as a string for the Select. When the Regions module is off the select
  // is hidden, but this still round-trips an area's existing region_id through
  // edit → save, so a save never silently detaches an area from its region.
  const [regionId, setRegionId] = useState<string>(NO_REGION);
  const [postcodesText, setPostcodesText] = useState('');
  // The list only returns a postcode PREVIEW per area, so the edit dialog fetches
  // the full set on demand; this tracks that load so the textarea shows progress.
  const [loadingPostcodes, setLoadingPostcodes] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  function openCreate() {
    setEditingId(null);
    setName('');
    setContractor('');
    setRegionId(NO_REGION);
    setPostcodesText('');
    setDialogOpen(true);
  }

  async function openEdit(areaItem: Area) {
    setEditingId(areaItem.id);
    setName(areaItem.name ?? '');
    setContractor(areaItem.contractor ?? '');
    setRegionId(areaItem.region_id != null ? String(areaItem.region_id) : NO_REGION);
    // Seed with the preview so the field isn't empty, then load the full list —
    // the list response only carries a bounded preview (areas can hold tens of
    // thousands of postcodes).
    setPostcodesText(areaItem.postcodes.join('\n'));
    setDialogOpen(true);

    if (areaItem.postcode_count > areaItem.postcodes.length) {
      setLoadingPostcodes(true);
      try {
        const full = await locationsService.getArea(areaItem.id);
        // Ignore if the user already switched to editing a different area.
        setEditingId((current) => {
          if (current === areaItem.id) setPostcodesText(full.postcodes.join('\n'));
          return current;
        });
      } catch (error: any) {
        showErrorToast('Could not load postcodes', error?.message || 'Please try again.');
      } finally {
        setLoadingPostcodes(false);
      }
    }
  }

  async function handleSave() {
    if (!name.trim()) return;
    const body = {
      name,
      contractor: contractor || null,
      region_id: regionId === NO_REGION ? null : Number(regionId),
      postcodes: parsePostcodes(postcodesText),
    };
    try {
      if (editingId) {
        await updateMutation.mutateAsync(body);
      } else {
        await createMutation.mutateAsync(body);
      }
      showSuccessToast(editingId ? 'Area updated.' : 'Area created.');
      setDialogOpen(false);
    } catch (error: any) {
      showErrorToast('Save failed', error?.message || 'An unexpected error occurred.');
    }
  }

  async function handleDelete() {
    if (deleteId === null) return;
    try {
      await deleteMutation.mutateAsync(deleteId);
      showSuccessToast('Area deleted.');
    } catch (error: any) {
      showErrorToast('Delete failed', error?.message || 'An unexpected error occurred.');
    } finally {
      setDeleteId(null);
    }
  }

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Areas"
        subtitle="Manage areas and the postcodes they cover"
        icon={<MapPin className="size-7 text-primary" />}
        actions={
          <Can action="create" subject="Area">
            <Button onClick={openCreate} className="text-white">
              <Plus className="w-4 h-4 mr-1.5" />
              Add Area
            </Button>
          </Can>
        }
      />

      <div className="pb-6">
        <div className="mb-4 relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={
              regionsEnabled ? 'Search areas, regions, postcodes...' : 'Search areas, postcodes...'
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                {regionsEnabled && <TableHead>Region</TableHead>}
                <TableHead>Postcodes</TableHead>
                {showActions && <TableHead className="w-[100px] text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={colSpan} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : areas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colSpan} className="text-center py-8 text-muted-foreground">
                    {debouncedSearch ? 'No areas match your search.' : 'No areas yet.'}
                  </TableCell>
                </TableRow>
              ) : (
                areas.map((areaItem: Area) => (
                  <TableRow key={areaItem.id} className="border-border hover:bg-muted/50">
                    <TableCell className="font-medium text-foreground">{areaItem.name}</TableCell>
                    {regionsEnabled && (
                      <TableCell className="text-muted-foreground">
                        {areaItem.region_name ?? '—'}
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {areaItem.postcodes.slice(0, 8).map((pc: string) => (
                          <Badge key={pc} variant="secondary" className="text-[10px]">
                            {pc}
                          </Badge>
                        ))}
                        {/* postcode_count is the true total; postcodes[] is only a
                            preview from the list endpoint. */}
                        {areaItem.postcode_count > 8 && (
                          <span className="text-xs text-muted-foreground">
                            +{areaItem.postcode_count - 8}
                          </span>
                        )}
                        {areaItem.postcode_count === 0 && (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                    </TableCell>
                    {showActions && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Can action="update" subject="Area">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(areaItem)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </Can>
                          <Can action="delete" subject="Area">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => setDeleteId(areaItem.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </Can>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Area' : 'Add Area'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="area-name">Name *</Label>
              <Input
                id="area-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Leeds"
              />
            </div>
            {regionsEnabled && (
              <div className="space-y-1.5">
                <Label htmlFor="area-region">Region</Label>
                <Select value={regionId} onValueChange={setRegionId}>
                  <SelectTrigger id="area-region">
                    <SelectValue placeholder="No region" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_REGION}>No region</SelectItem>
                    {regions.map((r) => (
                      <SelectItem key={r.id} value={String(r.id)}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="area-contractor">Contractor</Label>
              <Input
                id="area-contractor"
                value={contractor}
                onChange={(e) => setContractor(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="area-postcodes">Postcodes{loadingPostcodes && ' (loading…)'}</Label>
              <Textarea
                id="area-postcodes"
                value={postcodesText}
                onChange={(e) => setPostcodesText(e.target.value)}
                rows={5}
                placeholder="One per line, or comma-separated (e.g. LS1, LS2)"
                // Disabled while the full list loads so a save can't overwrite the
                // area's postcodes with the partial preview.
                disabled={loadingPostcodes}
              />
              <p className="text-[11px] text-muted-foreground">
                {parsePostcodes(postcodesText).length} postcode(s). Used to match jobs to teams by
                postcode.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              // Block save until the full postcode list has loaded, so an edit
              // never persists the partial preview and drops postcodes.
              disabled={!name.trim() || isSaving || loadingPostcodes}
              style={{ backgroundColor: '#E84A2B' }}
              className="text-white"
            >
              {isSaving ? 'Saving...' : editingId ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Area</AlertDialogTitle>
            <AlertDialogDescription>
              This area and its postcodes will be deleted. Teams assigned to it lose this coverage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
